<script setup>
/**
 * This component handles parsing and sending WhatsApp message templates.
 * It works as follows:
 * 1. Displays the template text with variable placeholders.
 * 2. Generates input fields for each variable in the template.
 * 3. Validates that all variables are filled before sending.
 * 4. Replaces placeholders with user-provided values.
 * 5. Emits events to send the processed message or reset the template.
 */
import { ref, computed, onMounted, watch } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { requiredIf } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import {
  buildTemplateParameters,
  allKeysRequired,
  replaceTemplateVariables,
  DEFAULT_LANGUAGE,
  DEFAULT_CATEGORY,
  COMPONENT_TYPES,
  MEDIA_FORMATS,
  findComponentByType,
} from 'dashboard/helper/templateHelper';
import {
  categorizeVariables,
  extractVariables,
} from 'dashboard/helper/templateVariableHelper';
import {
  parseLocationFromUrl,
  isValidLocation,
  formatLocationDisplay,
} from 'dashboard/helper/locationHelper';

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
    validator: value => {
      if (!value || typeof value !== 'object') return false;
      if (!value.components || !Array.isArray(value.components)) return false;
      return true;
    },
  },
});

const emit = defineEmits(['sendMessage', 'resetTemplate', 'back']);

const { t } = useI18n();

const processedParams = ref({});
const hoveredVariable = ref(null);
const locationUrl = ref('');

const languageLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.LANGUAGE')}: ${props.template.language || DEFAULT_LANGUAGE}`;
});

const categoryLabel = computed(() => {
  return `${t('WHATSAPP_TEMPLATES.PARSER.CATEGORY')}: ${props.template.category || DEFAULT_CATEGORY}`;
});

const headerComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.HEADER);
});

const bodyComponent = computed(() => {
  return findComponentByType(props.template, COMPONENT_TYPES.BODY);
});

const bodyText = computed(() => {
  return bodyComponent.value?.text || '';
});

const hasMediaHeader = computed(() =>
  MEDIA_FORMATS.includes(headerComponent.value?.format)
);

const formatType = computed(() => {
  const format = headerComponent.value?.format;
  return format ? format.charAt(0) + format.slice(1).toLowerCase() : '';
});

const isDocumentTemplate = computed(() => {
  return headerComponent.value?.format?.toLowerCase() === 'document';
});

const hasVariables = computed(() => {
  return bodyText.value?.match(/{{([^}]+)}}/g) !== null;
});

// Extract variable mappings from template metadata
const variableMappings = computed(() => {
  return props.template?.metadata?.variable_mappings || {};
});

// Get all detected variables from body text
const detectedVariables = computed(() => {
  return extractVariables(bodyText.value);
});

// Categorize variables as static or dynamic
const categorizedVariables = computed(() => {
  return categorizeVariables(detectedVariables.value, variableMappings.value);
});

// Check if template has any variable mappings configured
const hasVariableMappings = computed(() => {
  return Object.keys(variableMappings.value).length > 0;
});

// For old templates without mappings, create default mappings
const effectiveVariableMappings = computed(() => {
  if (hasVariableMappings.value) {
    return variableMappings.value;
  }

  // Create default mappings for old templates
  const defaultMappings = {};
  detectedVariables.value.forEach(position => {
    defaultMappings[position] = {
      position,
      type: 'static', // Default to static for old templates
      data_type: 'string',
      source: '',
      example_value: '',
      fallback_value: '',
    };
  });

  return defaultMappings;
});

// Get user-friendly data source label
const getDataSourceLabel = source => {
  if (!source) return '';

  const labels = {
    'contact.name': 'Contact Name',
    'contact.first_name': 'Contact First Name',
    'contact.last_name': 'Contact Last Name',
    'contact.email': 'Contact Email',
    'contact.phone_number': 'Contact Phone',
    'contact.city': 'Contact City',
    'contact.country': 'Contact Country',
    'contact.company': 'Contact Company',
    'conversation.id': 'Conversation ID',
    'conversation.assignee.name': 'Assignee Name',
    'conversation.inbox.name': 'Inbox Name',
    'account.name': 'Account Name',
    'account.domain': 'Account Domain',
  };

  // Check for custom attributes
  if (source.startsWith('contact.custom_attributes.')) {
    const attrName = source.replace('contact.custom_attributes.', '');
    return `Custom: ${attrName.charAt(0).toUpperCase() + attrName.slice(1)}`;
  }

  return labels[source] || source;
};

// Get appropriate input type based on data type
const getInputType = dataType => {
  if (!dataType) return 'text';

  switch (dataType) {
    case 'number':
    case 'currency':
      return 'number';
    case 'date_time':
      return 'datetime-local';
    default:
      return 'text';
  }
};

// Color palette - using darker shades for better contrast with white text
const variablePreviewColors = [
  'bg-blue-600 dark:bg-blue-700',
  'bg-green-600 dark:bg-green-700',
  'bg-purple-600 dark:bg-purple-700',
  'bg-amber-600 dark:bg-amber-700',
  'bg-pink-600 dark:bg-pink-700',
];

const variableLeftBorderColors = [
  'border-l-4 border-l-blue-400 dark:border-l-blue-500',
  'border-l-4 border-l-green-400 dark:border-l-green-500',
  'border-l-4 border-l-purple-400 dark:border-l-purple-500',
  'border-l-4 border-l-amber-400 dark:border-l-amber-500',
  'border-l-4 border-l-pink-400 dark:border-l-pink-500',
];

const variableCardBackgrounds = [
  'bg-white dark:bg-n-solid-2',
  'bg-white dark:bg-n-solid-2',
  'bg-white dark:bg-n-solid-2',
  'bg-white dark:bg-n-solid-2',
  'bg-white dark:bg-n-solid-2',
];

const variableHoverColors = [
  'hover:bg-blue-700 dark:hover:bg-blue-800',
  'hover:bg-green-700 dark:hover:bg-green-800',
  'hover:bg-purple-700 dark:hover:bg-purple-800',
  'hover:bg-amber-700 dark:hover:bg-amber-800',
  'hover:bg-pink-700 dark:hover:bg-pink-800',
];

// Render template with highlighted variables
const renderedTemplateWithHighlights = computed(() => {
  if (!bodyText.value) return '';

  const variables = detectedVariables.value;
  let result = bodyText.value;

  variables.forEach((key, index) => {
    const bgColorClass =
      variablePreviewColors[index % variablePreviewColors.length];
    const hoverClass = variableHoverColors[index % variableHoverColors.length];
    const textColorClass = 'text-white'; // White text for dark backgrounds
    const mapping = effectiveVariableMappings.value[key];
    const value = processedParams.value.body?.[key];

    // Determine the display value - ALWAYS prioritize custom value
    let displayValue;

    // Check if user has entered a custom value (even if it's 0 or empty string, if they typed it, use it)
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      displayValue = String(value);
    } else {
      // Only fall back to defaults if no custom value exists
      if (mapping?.type === 'dynamic') {
        displayValue = mapping.fallback_value || `{{${key}}}`;
      } else {
        displayValue =
          mapping?.example_value || mapping?.fallback_value || `{{${key}}}`;
      }
    }

    const placeholder = `{{${key}}}`;

    // If we are just showing the raw placeholder (no value, no example, no fallback),
    // and the mapping isn't explicitly dynamic with a specific source,
    // skip the HTML span wrapper to avoid "broken html" look in preview.
    // The user prefers seeing raw {{1}} in RED in this case.
    let highlighted;

    // Check if displayValue is just the placeholder itself
    const isRawPlaceholder = displayValue === `{{${key}}}`;

    if (isRawPlaceholder) {
      // Render as plain red text without badge styling
      highlighted = `<span class="text-n-ruby-9 font-bold" data-variable="${key}" title="Variable {{${key}}} - No value assigned">{{${key}}}</span>`;
    } else {
      const isHovered = hoveredVariable.value === key;
      const highlightClass = isHovered
        ? 'ring-2 ring-offset-1 ring-blue-400 dark:ring-blue-600'
        : '';
      highlighted = `<span class="${bgColorClass} ${textColorClass} ${hoverClass} ${highlightClass} px-1.5 py-0.5 rounded font-medium cursor-pointer transition-all" data-variable="${key}" title="{{${key}}}">${displayValue}</span>`;
    }

    result = result.replace(
      new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
      highlighted
    );
  });

  return result;
});

const renderedTemplate = computed(() => {
  return replaceTemplateVariables(bodyText.value, processedParams.value);
});

// Get color class for a variable - using left border with subtle tinted background
const getVariableColor = index => {
  const borderClass =
    variableLeftBorderColors[index % variableLeftBorderColors.length];
  const bgClass =
    variableCardBackgrounds[index % variableCardBackgrounds.length];
  return `${borderClass} ${bgClass}`;
};

// Get hover highlight class for variable card
const getVariableHighlight = key => {
  return hoveredVariable.value === key
    ? 'ring-2 ring-blue-400 dark:ring-blue-600'
    : '';
};

// Handle hover on preview variables
const handlePreviewHover = event => {
  const target = event.target;
  if (target.dataset.variable) {
    hoveredVariable.value = target.dataset.variable;
  }
};

const handlePreviewLeave = () => {
  hoveredVariable.value = null;
};

// Handle variable value update
const updateVariableValue = (key, value) => {
  if (!processedParams.value.body) {
    processedParams.value.body = {};
  }

  // Update the value
  processedParams.value.body[key] = value;

  // Force reactivity by creating a new reference for the body object
  processedParams.value.body = { ...processedParams.value.body };
};

const isFormInvalid = computed(() => {
  // No validation needed if template has no variables and no media header
  if (!hasVariables.value && !hasMediaHeader.value) return false;

  // For media header: validate based on media type
  if (hasMediaHeader.value) {
    const headerData = processedParams.value.header;
    const mediaType = headerComponent.value?.format;

    if (mediaType === 'LOCATION') {
      // For location templates, check if we have stored location or valid URL input
      const hasStoredLocation =
        storedLocation.value && isValidLocation(storedLocation.value);
      const hasValidParsedLocation =
        parsedLocation.value && isValidLocation(parsedLocation.value);

      if (!hasStoredLocation && !hasValidParsedLocation) {
        return true;
      }
    } else {
      // For other media types, check if template has stored media
      // If template has stored media, URL override is optional
      // If template has no stored media, URL is required
      const hasStoredMedia =
        props.template?.metadata?.media_blob_id ||
        props.template?.metadata?.media_url;

      // Only invalid if template has no stored media AND user hasn't provided URL
      // If template has stored media, form is valid (URL override is optional)
      if (!hasStoredMedia && !headerData?.media_url) {
        return true;
      }
    }
  }

  // For variables: only validate static variables that don't have example/fallback values
  if (hasVariables.value && processedParams.value.body) {
    // Check only static variables that require input
    const hasInvalidStaticVariable = Object.keys(
      processedParams.value.body
    ).some(key => {
      const mapping = effectiveVariableMappings.value[key];

      // Dynamic variables don't need input (they're auto-filled)
      if (
        hasVariableMappings.value &&
        categorizedVariables.value.dynamic.includes(key)
      ) {
        return false;
      }

      // Static variables: check if value is provided OR if there's a fallback/example
      const value = processedParams.value.body[key];
      const hasDefaultValue = mapping?.example_value || mapping?.fallback_value;

      // Only invalid if no value AND no default value
      return !value && !hasDefaultValue;
    });

    if (hasInvalidStaticVariable) return true;
  }

  // Validate button parameters (if any)
  if (processedParams.value.buttons) {
    const hasEmptyButtonParameter = processedParams.value.buttons.some(
      button => !button.parameter
    );
    if (hasEmptyButtonParameter) return true;
  }

  return false;
});

const v$ = useVuelidate(
  {
    processedParams: {
      requiredIfKeysPresent: requiredIf(hasVariables),
      allKeysRequired,
    },
  },
  { processedParams }
);

const initializeTemplateParameters = () => {
  processedParams.value = buildTemplateParameters(
    props.template,
    hasMediaHeader.value
  );

  // Initialize location from stored metadata
  if (headerComponent.value?.format === 'LOCATION' && storedLocation.value) {
    processedParams.value.header = {
      media_type: 'location',
      ...storedLocation.value,
    };
  }
};

const updateMediaUrl = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_url = value;
};

const updateMediaName = value => {
  processedParams.value.header ??= {};
  processedParams.value.header.media_name = value;
};

const storedLocation = computed(() => {
  return props.template?.metadata?.location || null;
});

const parsedLocation = computed(() => {
  if (!locationUrl.value) {
    return storedLocation.value;
  }

  const parsed = parseLocationFromUrl(locationUrl.value);
  return parsed && isValidLocation(parsed) ? parsed : storedLocation.value;
});

const locationDisplay = computed(() => {
  return formatLocationDisplay(parsedLocation.value);
});

const updateLocationUrl = value => {
  locationUrl.value = value;

  // Parse and update header if valid
  const location = parseLocationFromUrl(value);
  if (location && isValidLocation(location)) {
    processedParams.value.header ??= {};
    processedParams.value.header.latitude = location.latitude;
    processedParams.value.header.longitude = location.longitude;
    processedParams.value.header.name = location.name;
    processedParams.value.header.address = location.address;
  }
};

const sendMessage = () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  const { name, category, language, namespace } = props.template;

  // Extract static parameters for backend variable resolution
  const staticParams = {};
  if (hasVariableMappings.value) {
    categorizedVariables.value.static.forEach(position => {
      const value = processedParams.value.body?.[position];
      if (value !== undefined && value !== null) {
        staticParams[position] = value;
      }
    });
  }

  const payload = {
    message: renderedTemplate.value,
    templateParams: {
      name,
      category,
      language,
      namespace,
      processed_params: processedParams.value,
      static_params: staticParams, // Add static params for backend resolution
    },
  };
  emit('sendMessage', payload);
};

const resetTemplate = () => {
  emit('resetTemplate');
};

const goBack = () => {
  emit('back');
};

onMounted(initializeTemplateParameters);

watch(
  () => props.template,
  () => {
    initializeTemplateParameters();
    v$.value.$reset();
  },
  { deep: true }
);

const formatVariablePlaceholder = key => {
  return `{{${key}}}`;
};

defineExpose({
  processedParams,
  hasVariables,
  hasMediaHeader,
  isDocumentTemplate,
  headerComponent,
  renderedTemplate,
  v$,
  isFormInvalid,
  updateMediaUrl,
  updateMediaName,
  sendMessage,
  resetTemplate,
  goBack,
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-4 p-4 mb-4 rounded-lg bg-n-alpha-black2">
      <div class="flex justify-between items-center">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ template.name }}
        </h3>
        <span class="text-xs text-n-slate-11">
          {{ languageLabel }}
        </span>
      </div>

      <!-- Template Preview with Highlighted Variables -->
      <div
        class="p-3 rounded-lg bg-white dark:bg-n-solid-2 border border-n-weak"
      >
        <p class="text-xs font-medium text-n-slate-11 mb-2">Preview</p>
        <div
          class="text-sm whitespace-pre-wrap text-n-slate-12 leading-relaxed"
          @mouseover="handlePreviewHover"
          @mouseleave="handlePreviewLeave"
          v-html="renderedTemplateWithHighlights"
        />
      </div>

      <div class="text-xs text-n-slate-11">
        {{ categoryLabel }}
      </div>
    </div>

    <!-- Simplified Variable Inputs -->
    <div v-if="hasVariables || hasMediaHeader" class="space-y-4 mt-4">
      <!-- Media Header - Show for ALL media types -->
      <div v-if="hasMediaHeader" class="space-y-2">
        <!-- Location Template Fields -->
        <div v-if="headerComponent?.format === 'LOCATION'" class="space-y-3">
          <div>
            <label class="text-sm font-medium text-n-slate-12">
              📍
              {{
                t('WHATSAPP_TEMPLATES.PARSER.LOCATION_FIELDS.TITLE') ||
                'Send Location'
              }}
            </label>
            <p class="text-xs text-n-slate-11 mb-2">
              {{
                t('WHATSAPP_TEMPLATES.PARSER.LOCATION_FIELDS.DESCRIPTION') ||
                'This template will send a location to the contact'
              }}
            </p>
          </div>

          <!-- Show stored location if available -->
          <div
            v-if="storedLocation && isValidLocation(storedLocation)"
            class="flex items-start gap-2 p-3 rounded-lg bg-n-blue-2 border border-n-blue-6"
          >
            <span
              class="i-lucide-map-pin size-4 text-n-blue-9 flex-shrink-0 mt-0.5"
            />
            <div class="flex-1 min-w-0">
              <p class="text-xs font-medium text-n-blue-11 mb-1">
                Default Location
              </p>
              <p class="text-xs text-n-blue-10">{{ locationDisplay }}</p>
              <p class="text-xs text-n-slate-11 mt-2 italic">
                Optional: Paste a different Google Maps link below to change the
                location for this message
              </p>
            </div>
          </div>

          <!-- Location URL Input -->
          <div>
            <label class="text-xs font-medium text-n-slate-11 mb-1 block">
              {{
                storedLocation && isValidLocation(storedLocation)
                  ? 'Override Location (Optional)'
                  : 'Location Link (Required)'
              }}
            </label>
            <Input
              :model-value="locationUrl"
              type="url"
              placeholder="Paste Google Maps link here to change location..."
              @update:model-value="updateLocationUrl"
            />
            <div
              v-if="
                locationUrl && parsedLocation && isValidLocation(parsedLocation)
              "
              class="flex items-start gap-2 mt-2 p-2 bg-n-green-2 border border-n-green-6 rounded"
            >
              <span
                class="i-lucide-check-circle size-3.5 text-n-green-9 flex-shrink-0 mt-0.5"
              />
              <p class="text-xs text-n-green-10">
                New location will be sent:
                {{ formatLocationDisplay(parsedLocation) }}
              </p>
            </div>
            <div
              v-if="
                locationUrl &&
                (!parsedLocation || !isValidLocation(parsedLocation))
              "
              class="flex items-start gap-2 mt-2 p-2 bg-n-ruby-2 border border-n-ruby-6 rounded"
            >
              <span
                class="i-lucide-alert-circle size-3.5 text-n-ruby-9 flex-shrink-0 mt-0.5"
              />
              <p class="text-xs text-n-ruby-10">
                Unable to detect location. Please paste a valid Google Maps
                link.
              </p>
            </div>
          </div>
        </div>

        <!-- Other Media Types (Image, Video, Document) -->
        <div v-else>
          <label class="text-sm font-medium text-n-slate-12">
            {{ formatType }} URL
            <span class="text-xs font-normal text-n-slate-11">(Optional - override template media)</span>
          </label>
          <Input
            :model-value="processedParams.header?.media_url || ''"
            type="url"
            :placeholder="`Enter ${formatType.toLowerCase()} URL or leave empty to use template default`"
            @update:model-value="updateMediaUrl"
          />
          <Input
            v-if="isDocumentTemplate"
            :model-value="processedParams.header?.media_name || ''"
            type="text"
            :placeholder="
              t('WHATSAPP_TEMPLATES.PARSER.DOCUMENT_NAME_PLACEHOLDER') ||
              'Document filename (e.g., invoice.pdf)'
            "
            @update:model-value="updateMediaName"
          />
        </div>
      </div>

      <!-- Simplified Variables Section -->
      <div
        v-if="
          processedParams.body && Object.keys(processedParams.body).length > 0
        "
        class="space-y-3"
      >
        <p class="text-sm font-medium text-n-slate-12">Variables</p>

        <div
          v-for="(variable, key, index) in processedParams.body"
          :key="`body-${key}`"
          class="p-3 rounded-lg border border-n-weak transition-all cursor-pointer"
          :class="[getVariableColor(index), getVariableHighlight(key)]"
          @mouseenter="hoveredVariable = key"
          @mouseleave="hoveredVariable = null"
        >
          <!-- Variable Header -->
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-2">
              <span class="text-xs font-mono font-medium text-n-slate-12">
                {{ formatVariablePlaceholder(key) }}
              </span>
              <span
                v-if="
                  hasVariableMappings &&
                  categorizedVariables.dynamic.includes(key)
                "
                class="text-[10px] px-1.5 py-0.5 bg-n-green-3 text-n-green-11 rounded font-medium"
              >
                Auto-filled
              </span>
            </div>
          </div>

          <!-- Dynamic Variable Info -->
          <div
            v-if="
              hasVariableMappings && categorizedVariables.dynamic.includes(key)
            "
            class="text-xs text-n-slate-11 mb-2"
          >
            Source:
            {{ getDataSourceLabel(effectiveVariableMappings[key]?.source) }}
            <span v-if="effectiveVariableMappings[key]?.fallback_value">
              • Default: {{ effectiveVariableMappings[key]?.fallback_value }}
            </span>
          </div>

          <!-- Static Variable Default -->
          <div
            v-else-if="
              effectiveVariableMappings[key]?.example_value ||
              effectiveVariableMappings[key]?.fallback_value
            "
            class="text-xs text-n-slate-11 mb-2"
          >
            Default:
            {{
              effectiveVariableMappings[key]?.fallback_value ||
              effectiveVariableMappings[key]?.example_value
            }}
          </div>

          <!-- Optional Custom Value Input -->
          <Input
            :model-value="processedParams.body[key]"
            :type="getInputType(effectiveVariableMappings[key]?.data_type)"
            size="sm"
            :placeholder="
              hasVariableMappings && categorizedVariables.dynamic.includes(key)
                ? 'Optional override'
                : 'Custom value (optional)'
            "
            @update:model-value="value => updateVariableValue(key, value)"
          />
        </div>
      </div>

      <!-- Button Variables Section -->
      <div v-if="processedParams.buttons">
        <p class="mb-2.5 text-sm font-semibold">
          {{ t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETERS') }}
        </p>
        <div
          v-for="(button, index) in processedParams.buttons"
          :key="`button-${index}`"
          class="flex items-center mb-2.5"
        >
          <Input
            v-model="processedParams.buttons[index].parameter"
            type="text"
            class="flex-1"
            :placeholder="t('WHATSAPP_TEMPLATES.PARSER.BUTTON_PARAMETER')"
          />
        </div>
      </div>
      <p
        v-if="v$.$dirty && v$.$invalid"
        class="p-2.5 text-center rounded-md bg-n-ruby-9/20 text-n-ruby-9"
      >
        {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
      </p>
    </div>

    <slot
      name="actions"
      :send-message="sendMessage"
      :reset-template="resetTemplate"
      :go-back="goBack"
      :is-valid="!v$.$invalid"
      :disabled="isFormInvalid"
    />
  </div>
</template>
